import React, { useState, useRef, useEffect } from 'react';
import Calendar from '../../calendar.js';
import InputWrapper from './InputWrapper';

/**
 * Base component for calendar-based inputs (Date, ExpirationMonthYear, etc.)
 * Handles calendar state, click outside detection, and common UI structure
 */
const CalendarInputBase = ({
  field,
  fieldId,
  displayValue,
  onDateSelect,
  label,
  isRequired,
  error,
  placeholder,
  disabled,
  initialMonth,
  minDate,
  maxDate,
  iconInsideInput = false,
  language = 'fr'
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);

  const toggleCalendar = () => {
    setShowCalendar(prev => !prev);
  };

  const closeCalendar = () => {
    setShowCalendar(false);
  };

  const handleDateSelect = (date) => {
    if (date && onDateSelect) {
      // Validate min date
      if (minDate) {
        const min = new Date(minDate);
        min.setHours(0, 0, 0, 0);
        if (date < min) return;
      }
      // Validate max date
      if (maxDate) {
        const max = new Date(maxDate);
        max.setHours(23, 59, 59, 999);
        if (date > max) return;
      }
      onDateSelect(date);
      closeCalendar();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        const inputElement = event.target.closest('.formulaire-dynamique-calendar-input-base-container');
        if (!inputElement || !inputElement.contains(event.target)) {
          closeCalendar();
        }
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCalendar]);

  const calendarInitialMonth = initialMonth || new Date();
  const calendarMinDate = minDate ? new Date(minDate) : new Date("1900-01-01 00:00:00.000");
  const calendarMaxDate = maxDate ? new Date(maxDate) : null;

  const containerClassName = iconInsideInput 
    ? "formulaire-dynamique-calendar-input-base-container formulaire-dynamique-calendar-input-base-icon-inside"
    : "formulaire-dynamique-calendar-input-base-container";
  
  const inputClassName = iconInsideInput
    ? "profile-input clickable form-input formulaire-dynamique-calendar-input-base-input"
    : "profile-input clickable form-input";
  
  const iconClassName = iconInsideInput
    ? "profile-img formulaire-dynamique-calendar-input-base-icon-inside"
    : "profile-img";

  return (
    <InputWrapper field={field} label={label} isRequired={isRequired} error={error}>
      <div className={containerClassName}>
        <input
          type="text"
          className={inputClassName}
          value={displayValue || ''}
          readOnly={true}
          onMouseDown={e => toggleCalendar()}
          placeholder={placeholder}
          disabled={disabled || field.readOnly || false}
        />
        <img
          src={require('../../../../images/icons/calendar-2.png')}
          alt="calendrier"
          className={iconClassName}
          onMouseDown={e => toggleCalendar()}
        />
        {showCalendar && (
          <div
            className="calendar-wrapper"
            ref={calendarRef}
            tabIndex={0}
          >
            <Calendar
              onDayClick={handleDateSelect}
              initialMonth={calendarInitialMonth}
              minDate={calendarMinDate}
              maxDate={calendarMaxDate}
              allowChangeYear={true}
              language={language}
            />
          </div>
        )}
      </div>
    </InputWrapper>
  );
};

export default CalendarInputBase;
